import React, { useEffect, useState } from 'react';
import { resolveRegulatoryBadge } from '../../../../ManagementProfileTab/managementProfileColumnRenderers';
import profileStyles from './UserProfile.module.scss';
import { LOCKED_COLUMN_TOOLTIPS } from '../../../../ManagementProfileTab/ManagementProfileTabConstants';
import user from '../../../../../../../assets/user.png';
import LoadingSpinner from '../../../../../../../common/_custom/LoadingSpinner/LoadingSpinner';
import { fetchManagementProfileUserData } from '../../../../../../../store/dashboard/overview/overview_apiHelper';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { isSafeUrl, toTitleCase } from '../../../../../../../utils/utils';
import { Mixpanel } from '../../../../../../../utils/mixpanel';
import { AICA_MANAGEMENT_DIRECTOR_SECTION_CLICKED } from '../../../../../../../utils/constants/mixpanelEvents/investorEvents';
import linkedin from '../../../../../../../assets/linkedin.svg';
import clsx from 'clsx';
import dayjs from 'dayjs';
import {
    calendarMonthIcon,
    companyBlack,
    educationIcon,
    userBlack,
    mailIcon,
    callIcon,
    homeFillBlack,
    EDIT_PROFILE_ICON,
} from '../../../../../../../assets/hostedassets';
import CompanyLogoBadge from '../../../../../../../common/_custom/CompanyLogoBadge/CompanyLogoBadge';
import SideDrawerHeader from '../../../../../../../common/_custom/SideDrawer/components/SideDrawerHeader';
import {
    formatAgeLabel,
    formatAgeValue,
    formatDurationYearsPlus,
    formatFlexibleDateMonthYear,
    formatPreciseDurationLabel,
    getFlexibleDurationMonths,
    getUnionedDurationMonths,
} from '../../../../../../../utils/dateUtils';
import type { DirectorShip, RoleFields, WorkEx, Education, UserData } from './ManagementUserProfile.types';
import {
    AION_INGEST_LITIGATION_CLASSIFICATION_DATA,
    getEntryRole,
    getEducationSubline,
    pickEnriched,
    formatFollowersCount,
    getEntryDurationMonths,
    hasValidCreditScore,
    computeTenureDisplay,
    buildEntryDateRange,
} from './managementUserProfileUtils';
import ExpandableSummary from './ExpandableSummary';
import DrawerRow from './DrawerRow';
import RegulatoryCountRow from './RegulatoryCountRow';
import CreditBureauRow from './CreditBureauRow';
import ListRow from './ListRow';
import CollapsibleCard from './CollapsibleCard';
import NotAvailableRow from './NotAvailableRow';
import PlaceholderBadge from './PlaceholderBadge';
import EditDirectorDetailsModal from './EditDirectorDetailsModal/EditDirectorDetailsModal';
import Button from '../../../../../../../common/_custom/Button/Button';
import { useManagementUserProfileLogic } from './useManagementUserProfileLogic';

const ManagementUserProfile = ({
    showUserProfile,
    redirectToCreditBureau = (val, bureauType) => {},
    redirectToCompliances = () => {},
    redirectToRiskIndicators = redirectToCompliances,
    redirectToRegulatoryChecks,
    isPDFReport = false,
    dataPreloaded = false,
    regulatoryChecksLocked = false,
    editable = false,
    linkedinRefreshing = false,
    onEditSave,
}: {
    showUserProfile?: any;
    redirectToCreditBureau?: (directorId?: string, bureauType?: string) => void;
    redirectToCompliances?: () => void;
    redirectToRiskIndicators?: () => void;
    redirectToRegulatoryChecks?: () => void;
    isPDFReport?: boolean;
    dataPreloaded?: boolean;
    regulatoryChecksLocked?: boolean;
    editable?: boolean;
    linkedinRefreshing?: boolean;
    onEditSave?: (updates: Record<string, string>) => Promise<boolean>;
}) => {
    const params = useParams();
    const loggedInUser = useSelector((state: any) => state?.user?.userData);

    const { lastUpdatedLabel, isEditModalOpen, setEditModalOpen, handleEditSave } = useManagementUserProfileLogic({
        managementProfileId: showUserProfile?.managementProfileId,
        assesseeOrgId: params?.id,
        isPDFReport,
        onEditSave,
    });

    const trackDirectorSection = (section: string) => {
        Mixpanel.track(AICA_MANAGEMENT_DIRECTOR_SECTION_CLICKED, {
            section,
            parentId: loggedInUser?.investorOrgId,
            companyOrgId: params?.id,
            websiteUrl: window.location.href,
        });
    };

    const [loading, setLoading] = useState(!dataPreloaded);
    const [userDataRaw, setUserData] = useState<null | UserData>(dataPreloaded ? (showUserProfile as UserData) : null);
    const [litigationProcessed, setLitigationProcessed] = useState(dataPreloaded ? true : false);

    /**
     * getManagementProfiles returns the LinkedIn-enriched half of a director under
     * `customFields`, and leaves the identically-named top-level fields empty — every
     * profile has `workExperience: []`, `education: []`, `linkedinUrl: null`,
     * `followersCount: null`, `imageUrl: null`, `subTitle: null` regardless of how much
     * LinkedIn data was actually matched. Reading only the top level is why these sections
     * looked permanently empty.
     *
     * customFields wins where it has something; the top-level field stays as the fallback
     * so a payload that ever does populate it (or a different endpoint) keeps working.
     * `headline` is the enriched name for what the top level calls `subTitle`.
     */
    const userData: UserData | null = userDataRaw && {
        ...userDataRaw,
        workExperience: pickEnriched(userDataRaw.customFields?.workExperience, userDataRaw.workExperience),
        education: pickEnriched(userDataRaw.customFields?.education, userDataRaw.education),
        linkedinUrl: userDataRaw.linkedinUrl || userDataRaw.customFields?.linkedinUrl || undefined,
        followersCount: formatFollowersCount(userDataRaw.customFields?.followersCount ?? userDataRaw.followersCount),
        imageUrl: userDataRaw.customFields?.imageUrl || userDataRaw.imageUrl,
        subTitle: userDataRaw.customFields?.headline || userDataRaw.subTitle,
    };
    // Right-column card collapse state — all five sections default expanded.
    // Not used by the PDF branch (that path stays fully expanded/static).
    const [expandedCards, setExpandedCards] = useState({
        directorInfo: true,
        regulatoryInfo: true,
        workExperience: true,
        education: true,
        otherDirectorships: true,
    });
    const toggleCard = (key: keyof typeof expandedCards) => setExpandedCards((prev) => ({ ...prev, [key]: !prev[key] }));

    const showLinkedinSection = !!(
        userData?.linkedinUrl ||
        userData?.followersCount ||
        userData?.workExperience?.length ||
        userData?.education?.length
    );

    const showDirectorshipsSection = !!(userData?.currentEntities?.length || userData?.formerEntities?.length);

    const hideSectionForPDF = (value) => {
        if (isPDFReport && (value === undefined || value === null)) return true;
        return false;
    };

    /**
     * Whether the PDF's Regulatory Information section has anything to say. A zero count IS
     * something to say ("screened, nothing found") — only an absent one isn't. This has to be a
     * real boolean rather than the `a || b || c || d` chain it replaces: that chain evaluates to
     * its LAST operand when every operand is falsy, so `legalCasesCount: 0` with an undefined
     * `amlPepRiskCount` came out as `undefined` and hid a section that did have data. Which
     * counts happened to be present decided whether the section appeared.
     *
     * The score is read through `hasValidCreditScore` — the same check CreditBureauRow renders
     * on — rather than a bare `!Number.isNaN(Number(score))`, because `Number(null)` and
     * `Number('')` are both 0: those made this claim there was data while the row itself drew
     * nothing, so the PDF got an empty section.
     */
    /*
        RECUR-27829. The drawer reports the same verdict as the table cell, from the same
        function, so the two cannot disagree — previously this row read a flat count that no
        longer exists and rendered a dash beside a table cell reading "2 Confirmed".
    */
    const regulatoryBadge = resolveRegulatoryBadge({ amlCount: userData?.amlCount });

    const hasRegulatoryDataForPDF =
        hasValidCreditScore(userData?.creditScore) ||
        userData?.legalCasesCount != null ||
        userData?.riskIndicatorCount != null ||
        regulatoryBadge.count != null;

    useEffect(() => {
        if (dataPreloaded) return;
        showUserProfile && fetchData();
    }, [showUserProfile]);

    useEffect(() => {
        if (dataPreloaded && showUserProfile) setUserData(showUserProfile as UserData);
    }, [dataPreloaded, showUserProfile]);

    const fetchData = () => {
        fetchManagementProfileUserData({
            orgId: params.id || '',
            name: showUserProfile?.name,
            entityId: showUserProfile?.din,
            taskStepsList: [AION_INGEST_LITIGATION_CLASSIFICATION_DATA],
        })
            .then((res) => {
                let taskTrackerData = res?.taskTrackerData || {};
                let profileData = res?.managementProfile?.profile || {};
                setUserData(profileData);

                if (
                    AION_INGEST_LITIGATION_CLASSIFICATION_DATA in taskTrackerData &&
                    taskTrackerData[AION_INGEST_LITIGATION_CLASSIFICATION_DATA] !== 'TODO'
                ) {
                    setLitigationProcessed(true);
                }
            })
            .catch((err) => {
                console.error('Error in fetching management profile data', err);
            })
            .finally(() => setLoading(false));
    };

    const NoData = () => <span className={profileStyles.Nodata}>No data available</span>;

    // ── Two-column (on-screen) derived state ──────────────────────────────
    const ageLabel = formatAgeLabel(userData?.age);
    const ageValue = formatAgeValue(userData?.age);
    // linkedinUrl is API-supplied (customFields.linkedinUrl) and therefore untrusted — a
    // javascript: value would execute on click, so every use goes through this instead of the
    // raw field. isSafeUrl allows https:/mailto: only.
    const safeLinkedInUrl = userData?.linkedinUrl && isSafeUrl(userData.linkedinUrl) ? userData.linkedinUrl : undefined;
    const showLinkedInCard = !!(safeLinkedInUrl || userData?.followersCount || userData?.subTitle);
    const hasWorkExperience = !!userData?.workExperience?.length;
    const hasEducation = !!userData?.education?.length;
    const hasOtherDirectorships = showDirectorshipsSection;
    /**
     * Work Experience and Education come only from the LinkedIn enrichment, and it hasn't
     * run for most companies yet — `customFields` comes back empty. An empty card for a
     * feature that simply isn't switched on reads as broken rather than as missing data,
     * so these two are hidden outright until there is something to show.
     *
     * Other Directorships is MCA-derived, not LinkedIn, so it keeps rendering with its
     * "Not available" row — an underwriter seeing no other directorships is a real fact.
     */
    const showWorkExperienceList = hasWorkExperience;
    const showEducationList = hasEducation;
    const showOtherDirectorshipsList = true;

    const workExRangesForUnion = (userData?.workExperience || [])
        .map((exp, i) => (exp?.endDate || i === 0 ? { start: exp?.startDate, end: exp?.endDate } : null))
        .filter((range): range is { start: string | undefined; end: string | undefined } => range !== null);
    const workExperienceTotalMonths = workExRangesForUnion.length ? getUnionedDurationMonths(workExRangesForUnion) : null;
    const workExperienceStatLabel = formatDurationYearsPlus(workExperienceTotalMonths);

    // The stat pair appears when EITHER half has something to say; the other half then
    // shows "-". Requiring both would drop the directorship count for the many directors
    // who have no work-experience data at all.
    const showStatRow = showDirectorshipsSection || !!workExperienceStatLabel;

    // Tenure shows the computed duration AND the raw range side by side
    // ("12+ years (May 2024-Present)"). Each half degrades independently —
    // show whichever one is actually computable, never a blank/NaN gap.
    const { durationLabel: tenureDurationLabel, rangeLabel: tenureRangeLabel } = computeTenureDisplay(
        userData?.tenureBeginDate,
        userData?.tenureEndDate,
        userData?.isActive,
    );
    const hasTenureDuration = !!tenureDurationLabel;
    const showTenureRow = hasTenureDuration || !!tenureRangeLabel;

    const showDirectorInfoCard = !!(
        (userData?.isActive !== null && userData?.isActive !== undefined) ||
        showUserProfile?.designation ||
        showTenureRow ||
        userData?.din ||
        userData?.pan
    );

    // The Regulatory card always renders: each of its four rows shows "-" when its
    // value is absent, rather than disappearing. An empty regulatory section is
    // itself meaningful information for an underwriter. Each RegulatoryCountRow
    // resolves its own state, so there's nothing to pre-compute here.
    const showRegulatoryInfoCard = true;

    return (
        <div className={clsx(profileStyles.UserProfile, { [profileStyles.PDFReport]: isPDFReport })}>
            {/* The two-column drawer has no full-width header — the left rail runs flush
                to the top (avatar first), and "Director Details" lives inside the right
                column's own header (see RightColumnHeader below). Only the PDF/print path
                keeps this full-width bar, since it stays single-column. */}
            {isPDFReport && (
                <div className={profileStyles.Heading}>
                    <div className={profileStyles.HeadingTitle}>
                        Management Profile<span className={profileStyles.Name}> | {toTitleCase(showUserProfile?.name)}</span>
                    </div>
                    <div className={profileStyles.HeadingRight}></div>
                </div>
            )}

            <div className={clsx(profileStyles.Body, { [profileStyles.BodyNoHeading]: !isPDFReport })}>
                {loading ? (
                    <LoadingSpinner color={'#6021b3'} height={'30px'} style={{ marginTop: '40px' }} />
                ) : isPDFReport ? (
                    <div className={profileStyles.Container}>
                        <div className={profileStyles.Top}>
                            <div className={profileStyles.Image}>
                                <img src={userData?.imageUrl || user} alt="profile" width={'100%'} />
                            </div>

                            <div>
                                <div className={profileStyles.Name} style={{ fontSize: '16px' }}>
                                    {userData?.name || showUserProfile?.name || '-'}
                                </div>
                                <div className={profileStyles.InfoPills}>
                                    {userData?.dob && (
                                        <div className={profileStyles.Pill} style={{ fontSize: '11px' }}>
                                            DOB:&nbsp;
                                            <span className={profileStyles.Black}>{userData?.dob}&nbsp;</span>
                                            <>{userData?.age && `(${userData?.age})`}</>
                                        </div>
                                    )}
                                    {userData?.gender && (
                                        <div className={profileStyles.Pill} style={{ fontSize: '11px' }}>
                                            <img src={userBlack} alt={''} height={'16px'} />
                                            <span className={profileStyles.Black}>{toTitleCase(userData?.gender)}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {showUserProfile?.managementProfileSummary && (
                            <div className={profileStyles.ProfileSummary}>{showUserProfile.managementProfileSummary}</div>
                        )}

                        <div className={profileStyles.SectionHeading} style={{ fontSize: '11px' }}>
                            <div></div>
                            <div>Director Information</div>
                        </div>
                        <div className={profileStyles.DataRows} style={{ fontSize: '11px' }}>
                            {!hideSectionForPDF(userData?.isActive) && (
                                <div className={profileStyles.Row}>
                                    <div className={profileStyles.Key}>Status</div>
                                    <div
                                        className={clsx(profileStyles.Value, {
                                            [profileStyles.Active]: true,
                                            [profileStyles.Former]: !userData?.isActive,
                                        })}
                                    >
                                        {userData?.isActive === null || userData?.isActive === undefined ? (
                                            <NoData />
                                        ) : (
                                            <div
                                                className={clsx({
                                                    [profileStyles.Active]: userData?.isActive,
                                                    [profileStyles.Former]: !userData?.isActive,
                                                })}
                                            >
                                                {userData?.isActive ? 'Active' : 'Former'}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                            {!hideSectionForPDF(showUserProfile?.designation) && (
                                <div className={profileStyles.Row}>
                                    <div className={profileStyles.Key}>Designation</div>
                                    <div className={profileStyles.Value}>{showUserProfile?.designation || <NoData />}</div>
                                </div>
                            )}
                            {!hideSectionForPDF(userData?.directorSince) && (
                                <div className={profileStyles.Row}>
                                    <div className={profileStyles.Key}>Tenure</div>
                                    <div className={profileStyles.Value} style={{ padding: 0 }}>
                                        <div className={profileStyles.DirectorShip}>
                                            {userData?.directorSince || <NoData />}

                                            <div className={profileStyles.Dates}>
                                                {(userData?.isActive === null || userData?.isActive === undefined) &&
                                                !userData?.tenureBeginDate &&
                                                !userData?.tenureEndDate ? (
                                                    'NA'
                                                ) : (
                                                    <>
                                                        (
                                                        {userData?.tenureBeginDate && dayjs(userData?.tenureBeginDate).isValid()
                                                            ? dayjs(userData?.tenureBeginDate).format('MMM YYYY')
                                                            : 'NA'}
                                                        -{' '}
                                                        {userData?.tenureEndDate && dayjs(userData?.tenureEndDate).isValid()
                                                            ? dayjs(userData?.tenureEndDate).format('MMM YYYY')
                                                            : userData?.isActive
                                                            ? 'Present'
                                                            : 'NA'}
                                                        )
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {!hideSectionForPDF(userData?.din) && (
                                <div className={profileStyles.Row}>
                                    <div className={profileStyles.Key}>DIN No.</div>
                                    <div className={profileStyles.Value}>{userData?.din || <NoData />}</div>
                                </div>
                            )}

                            {!hideSectionForPDF(userData?.pan) && (
                                <div className={profileStyles.Row}>
                                    <div className={profileStyles.Key}>PAN</div>
                                    <div className={profileStyles.Value}>{userData?.pan || <NoData />}</div>
                                </div>
                            )}
                        </div>

                        {!hideSectionForPDF(userData?.phone || userData?.email || userData?.address) && (
                            <div className={profileStyles.SectionHeading} style={{ fontSize: '11px' }}>
                                <div></div>
                                <div> Contact Information</div>
                            </div>
                        )}
                        {!hideSectionForPDF(userData?.phone || userData?.email || userData?.address) && (
                            <div className={profileStyles.DataRows} style={{ fontSize: '11px' }}>
                                {!hideSectionForPDF(userData?.phone) && (
                                    <div className={profileStyles.Row}>
                                        <div className={profileStyles.Key}>Phone No.</div>
                                        <div className={profileStyles.Value}>{userData?.phone || <NoData />}</div>
                                    </div>
                                )}
                                {!hideSectionForPDF(userData?.email) && (
                                    <div className={profileStyles.Row}>
                                        <div className={profileStyles.Key}>Email</div>
                                        <div className={profileStyles.Value}>{userData?.email || <NoData />}</div>
                                    </div>
                                )}
                                {!hideSectionForPDF(userData?.address) && (
                                    <div className={profileStyles.Row}>
                                        <div className={profileStyles.Key}>Address</div>
                                        <div className={profileStyles.Value}>{userData?.address || <NoData />}</div>
                                    </div>
                                )}
                            </div>
                        )}

                        {(!isPDFReport || hasRegulatoryDataForPDF) && (
                            <div className={profileStyles.SectionHeading} style={{ fontSize: '11px' }}>
                                <div></div>
                                <div>Regulatory Information</div>
                            </div>
                        )}
                        {(!isPDFReport || hasRegulatoryDataForPDF) && (
                            <div className={profileStyles.DataRows} style={{ fontSize: '11px' }}>
                                {/* Mixpanel section tracking came from RECUR-27110 on the old
                                    markup this replaced — preserved on every click-through. */}
                                <CreditBureauRow
                                    score={userData?.creditScore}
                                    onClick={() => {
                                        trackDirectorSection('creditBureau');
                                        redirectToCreditBureau?.(userData?.directorId, userData?.creditBureau);
                                    }}
                                    isPDFReport={isPDFReport}
                                />
                                <RegulatoryCountRow
                                    label="Legal Cases"
                                    count={userData?.legalCasesCount}
                                    notReady={!litigationProcessed}
                                    unitSingular="Case"
                                    unitPlural="Cases"
                                    zeroLabel="No match found"
                                    onClickIfPositive={() => {
                                        trackDirectorSection('legalCases');
                                        redirectToCompliances?.();
                                    }}
                                    isPDFReport={isPDFReport}
                                />
                                <RegulatoryCountRow
                                    label="Risk Alerts"
                                    count={userData?.riskIndicatorCount}
                                    unitSingular="Alert"
                                    unitPlural="Alerts"
                                    zeroLabel="No alerts found"
                                    onClickIfPositive={() => {
                                        trackDirectorSection('riskIndicators');
                                        redirectToRiskIndicators?.();
                                    }}
                                    isPDFReport={isPDFReport}
                                />
                                <RegulatoryCountRow
                                    label="Regulatory Checks"
                                    count={regulatoryBadge.count}
                                    badge={regulatoryBadge}
                                    locked={regulatoryChecksLocked}
                                    unitSingular="Indicator"
                                    unitPlural="Indicators"
                                    zeroLabel="No indicators found"
                                    onClickIfPositive={() => {
                                        trackDirectorSection('regulatoryChecks');
                                        redirectToRegulatoryChecks?.();
                                    }}
                                    isPDFReport={isPDFReport}
                                    mutedLabel
                                    lockedTooltip={LOCKED_COLUMN_TOOLTIPS.regulatoryChecks}
                                />
                            </div>
                        )}

                        {showDirectorshipsSection && (
                            <>
                                <div className={profileStyles.SectionHeading} style={{ fontSize: '11px' }}>
                                    <div></div>
                                    <div>Other Directorships</div>
                                </div>
                                <div className={profileStyles.DataRows} style={{ fontSize: '11px' }}>
                                    <div className={profileStyles.Row}>
                                        <div className={profileStyles.Key}>Present Directorship</div>
                                        <div
                                            className={profileStyles.Value}
                                            style={{
                                                padding: userData?.currentEntities?.length ? '0' : '',
                                            }}
                                        >
                                            {userData?.currentEntities?.length ? (
                                                userData?.currentEntities?.map((directorship: DirectorShip, i) => {
                                                    let fromDate = directorship?.fromDate
                                                        ? dayjs(directorship?.fromDate).format('MMM YYYY')
                                                        : null;
                                                    let toDate = directorship?.toDate
                                                        ? dayjs(directorship?.toDate).format('MMM YYYY')
                                                        : null;
                                                    return (
                                                        directorship?.entityName && (
                                                            <div className={profileStyles.DirectorShip} key={i}>
                                                                {toTitleCase(directorship?.entityName)}

                                                                <div className={profileStyles.Dates}>
                                                                    ({fromDate || 'NA'} - {toDate || 'Present'})
                                                                </div>
                                                            </div>
                                                        )
                                                    );
                                                })
                                            ) : (
                                                <span className={profileStyles.Nodata}>No other present directorships</span>
                                            )}
                                        </div>
                                    </div>
                                    <div className={profileStyles.Row}>
                                        <div className={profileStyles.Key}>Past Directorships</div>
                                        <div
                                            className={profileStyles.Value}
                                            style={{
                                                padding: userData?.formerEntities?.length ? '0' : '',
                                            }}
                                        >
                                            {userData?.formerEntities?.length ? (
                                                userData?.formerEntities?.map((directorship: DirectorShip, i) => {
                                                    let fromDate = directorship?.fromDate
                                                        ? dayjs(directorship?.fromDate).format('MMM YYYY')
                                                        : null;
                                                    let toDate = directorship?.toDate
                                                        ? dayjs(directorship?.toDate).format('MMM YYYY')
                                                        : null;
                                                    return (
                                                        directorship?.entityName && (
                                                            <div className={profileStyles.DirectorShip} key={i}>
                                                                {toTitleCase(directorship?.entityName)}

                                                                <div className={profileStyles.Dates}>
                                                                    ({fromDate || 'NA'} - {toDate || 'NA'})
                                                                </div>
                                                            </div>
                                                        )
                                                    );
                                                })
                                            ) : (
                                                <span className={profileStyles.Nodata}>No other past directorships</span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}

                        {showLinkedinSection && (
                            <>
                                <div className={profileStyles.SectionHeading} style={{ fontSize: '11px' }}>
                                    <div></div>
                                    <div>Linkedin Profile</div>
                                </div>
                                <div className={profileStyles.DataRows} style={{ fontSize: '11px' }}>
                                    <div className={profileStyles.Row}>
                                        <div className={profileStyles.Key}>Linkedin Profile</div>
                                        <div className={profileStyles.Value}>
                                            {safeLinkedInUrl ? (
                                                <a
                                                    className={clsx(profileStyles.Flex, profileStyles.PdfLinkedInLink)}
                                                    href={safeLinkedInUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    <img src={linkedin} alt={''} />
                                                    {userData?.followersCount
                                                        ? `${userData?.followersCount} Followers`
                                                        : safeLinkedInUrl && 'View Profile'}
                                                </a>
                                            ) : (
                                                <NoData />
                                            )}
                                        </div>
                                    </div>
                                    <div className={profileStyles.Row}>
                                        <div className={profileStyles.Key}>Linkedin Headline</div>
                                        <div className={profileStyles.Value}>{userData?.subTitle || <NoData />}</div>
                                    </div>
                                    <div className={profileStyles.Row}>
                                        <div className={profileStyles.Key}>Work Experience</div>
                                        <div
                                            className={profileStyles.Value}
                                            style={{
                                                padding: userData?.workExperience?.length ? 0 : '',
                                            }}
                                        >
                                            {userData?.workExperience?.length ? (
                                                userData?.workExperience?.map((exp: WorkEx, i) => {
                                                    let fromDate = formatFlexibleDateMonthYear(exp?.startDate);
                                                    let toDate = formatFlexibleDateMonthYear(exp?.endDate);
                                                    return (
                                                        <div className={profileStyles.DirectorShip} key={i}>
                                                            {exp.companyName}

                                                            <div className={profileStyles.Dates}>
                                                                ({fromDate || 'NA'} - {toDate ? toDate : i === 0 ? 'Present' : 'NA'})
                                                            </div>
                                                        </div>
                                                    );
                                                })
                                            ) : (
                                                <NoData />
                                            )}
                                        </div>
                                    </div>
                                    <div className={profileStyles.Row}>
                                        <div className={profileStyles.Key}>Education</div>
                                        <div
                                            className={profileStyles.Value}
                                            style={{
                                                padding: userData?.education?.length ? '0' : '',
                                            }}
                                        >
                                            {userData?.education?.length ? (
                                                userData?.education?.map((ed: Education, i) => {
                                                    let fromDate = formatFlexibleDateMonthYear(ed?.startDate);
                                                    let toDate = formatFlexibleDateMonthYear(ed?.endDate);
                                                    return (
                                                        <div className={profileStyles.DirectorShip} key={i}>
                                                            {ed?.schoolName}

                                                            <div className={profileStyles.Dates}>
                                                                ({fromDate || 'NA'} - {toDate || 'NA'})
                                                            </div>
                                                        </div>
                                                    );
                                                })
                                            ) : (
                                                <NoData />
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                ) : (
                    <div className={profileStyles.TwoColumn}>
                        <div className={profileStyles.DrawerHeaderFull}>
                            <SideDrawerHeader
                                rightSlot={
                                    lastUpdatedLabel ? (
                                        <span className={profileStyles.HeaderLastUpdated}>{lastUpdatedLabel}</span>
                                    ) : undefined
                                }
                            >
                                <span className={profileStyles.DrawerTitle}>Director Details</span>
                            </SideDrawerHeader>
                        </div>

                        <div className={profileStyles.LeftRail}>
                            <div className={profileStyles.RailIdentity}>
                                <div className={profileStyles.RailIdentityTop}>
                                    <div className={profileStyles.AvatarCircle}>
                                        <img
                                            src={userData?.imageUrl || user}
                                            alt="profile"
                                            width={'100%'}
                                            onError={({ currentTarget }) => {
                                                currentTarget.onerror = null;
                                                currentTarget.src = user;
                                            }}
                                        />
                                    </div>
                                    {editable && onEditSave && userData && (
                                        <Button
                                            text="Edit Details"
                                            variant="secondary"
                                            size="small"
                                            type="button"
                                            icon={EDIT_PROFILE_ICON}
                                            onClick={() => setEditModalOpen(true)}
                                            className={profileStyles.EditDetailsButton}
                                            iconSize={{
                                                height: '1.25rem',
                                                width: '1.25rem',
                                            }}
                                        />
                                    )}
                                </div>
                                <div className={profileStyles.RailName}>{toTitleCase(userData?.name || showUserProfile?.name) || '-'}</div>
                                {(ageLabel || userData?.dob || userData?.gender) && (
                                    <div className={profileStyles.RailPills}>
                                        {userData?.dob ? (
                                            <div className={profileStyles.Pill}>
                                                <img src={calendarMonthIcon} className={profileStyles.PillIcon} alt="" />
                                                <span className={profileStyles.Black}>{`DOB: ${userData.dob}${
                                                    ageValue ? ` (${ageValue})` : ''
                                                }`}</span>
                                            </div>
                                        ) : (
                                            ageLabel && (
                                                <div className={profileStyles.Pill}>
                                                    <img src={calendarMonthIcon} className={profileStyles.PillIcon} alt="" />
                                                    <span className={profileStyles.Black}>{ageLabel}</span>
                                                </div>
                                            )
                                        )}
                                        {userData?.gender && (
                                            <div className={profileStyles.Pill}>
                                                <img src={userBlack} alt={''} height={'16px'} />
                                                <span className={profileStyles.Black}>{toTitleCase(userData.gender)}</span>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Stat cards, LinkedIn card and Overview are one group with
                                an even 16px rhythm — matches Figma 31776-11454. */}
                            {(showStatRow || showLinkedInCard || showUserProfile?.managementProfileSummary) && (
                                <div className={profileStyles.RailCards}>
                                    {/* Each card is gated on its own data now. The Work Experience card is
                                        LinkedIn-derived, so it disappears entirely rather than showing an
                                        empty state for an enrichment that hasn't run. When only one card
                                        survives it keeps its 59px height and spans the rail. */}
                                    {showStatRow && (
                                        <div className={profileStyles.StatRow}>
                                            {showDirectorshipsSection && (
                                                <div className={clsx(profileStyles.StatCard, profileStyles.StatCardGreen)}>
                                                    <div className={profileStyles.StatLabel}>Directorships</div>
                                                    <div className={profileStyles.StatValue}>
                                                        <span className={profileStyles.StatActive}>
                                                            {`${userData?.currentEntities?.length || 0} active`}
                                                        </span>
                                                        <span className={profileStyles.StatDot} />
                                                        <span className={profileStyles.StatPast}>
                                                            {`${userData?.formerEntities?.length || 0} past`}
                                                        </span>
                                                    </div>
                                                </div>
                                            )}
                                            {!!workExperienceStatLabel && (
                                                <div className={clsx(profileStyles.StatCard, profileStyles.StatCardBlue)}>
                                                    <div className={profileStyles.StatLabel}>Work Experience</div>
                                                    <div className={clsx(profileStyles.StatValue, profileStyles.StatBlueValue)}>
                                                        {workExperienceStatLabel}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {showLinkedInCard &&
                                        (() => {
                                            // Follower count if we have one; otherwise "View Profile", but only when
                                            // there's actually a profile to view.
                                            const linkedInValueLabel = userData?.followersCount
                                                ? `${userData.followersCount} Followers`
                                                : safeLinkedInUrl
                                                ? 'View Profile'
                                                : null;
                                            const cardContent = (
                                                <>
                                                    <div className={profileStyles.LinkedInTopRow}>
                                                        <span className={profileStyles.LinkedInLabel}>LinkedIn</span>
                                                        {/* Only render the mark when it has a label beside it. With just a
                                                            headline and no URL or follower count, both arms of the old
                                                            ternary were undefined and this rendered a bare icon. */}
                                                        {linkedInValueLabel && (
                                                            <span className={profileStyles.LinkedInLink}>
                                                                <img src={linkedin} alt={''} height={'14px'} />
                                                                {linkedInValueLabel}
                                                            </span>
                                                        )}
                                                    </div>
                                                    {userData?.subTitle && (
                                                        <div className={profileStyles.LinkedInHeadline}>{userData.subTitle}</div>
                                                    )}
                                                </>
                                            );

                                            // The WHOLE card is the link when we have a profile URL — a single
                                            // anchor rather than a nested one, so the label, follower count and
                                            // headline are all part of one hit target and one tab stop.
                                            return safeLinkedInUrl ? (
                                                <a
                                                    className={clsx(profileStyles.LinkedInCard, profileStyles.LinkedInCardLink)}
                                                    href={safeLinkedInUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    aria-label={`Open LinkedIn profile${
                                                        userData?.followersCount ? ` — ${userData.followersCount} followers` : ''
                                                    }`}
                                                >
                                                    {cardContent}
                                                </a>
                                            ) : (
                                                <div className={profileStyles.LinkedInCard}>{cardContent}</div>
                                            );
                                        })()}
                                    {showUserProfile?.managementProfileSummary && (
                                        <div className={profileStyles.OverviewSection}>
                                            <div className={profileStyles.RailSectionLabel}>Overview</div>
                                            <ExpandableSummary text={showUserProfile.managementProfileSummary} />
                                        </div>
                                    )}
                                </div>
                            )}

                            {(userData?.phone || userData?.email || userData?.address) && (
                                <div className={profileStyles.ContactSection}>
                                    <div className={profileStyles.RailDivider} />
                                    <div className={profileStyles.RailSectionLabel}>Contact</div>
                                    {userData?.phone && (
                                        <div className={profileStyles.ContactRow}>
                                            <img src={callIcon} alt={''} height={'18px'} />
                                            <span>{userData.phone}</span>
                                        </div>
                                    )}
                                    {userData?.email && (
                                        <div className={profileStyles.ContactRow}>
                                            <img src={mailIcon} alt={''} height={'18px'} />
                                            <span>{userData.email}</span>
                                        </div>
                                    )}
                                    {userData?.address && (
                                        <div className={profileStyles.ContactRow}>
                                            <img src={homeFillBlack} alt={''} height={'18px'} />
                                            <span>{userData.address}</span>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className={profileStyles.RightColumn}>
                            {isEditModalOpen && userData && onEditSave && (
                                <EditDirectorDetailsModal
                                    open
                                    onClose={() => setEditModalOpen(false)}
                                    director={userData}
                                    onSave={handleEditSave}
                                    linkedinRefreshing={linkedinRefreshing}
                                />
                            )}

                            <div className={profileStyles.RightColumnBody}>
                                {/* Scroll lives on the parent; this inner wrapper
                                    owns the 24px inset so the scrollbar can never
                                    eat it and clip the cards at the screen edge. */}
                                <div className={profileStyles.CardsWrapper}>
                                    {showDirectorInfoCard && (
                                        <CollapsibleCard
                                            id="director-information-panel"
                                            title="Director Information"
                                            expanded={expandedCards.directorInfo}
                                            onToggle={() => toggleCard('directorInfo')}
                                        >
                                            {/* All five go through DrawerRow, same as the
                                                Regulatory rows below — one definition of the
                                                label/value grid and the dash-when-absent rule. */}
                                            <DrawerRow
                                                label="Status"
                                                value={
                                                    userData?.isActive === null || userData?.isActive === undefined ? undefined : (
                                                        <div
                                                            className={clsx({
                                                                [profileStyles.Active]: userData.isActive,
                                                                [profileStyles.Former]: !userData.isActive,
                                                            })}
                                                        >
                                                            {userData.isActive ? 'Active' : 'Former'}
                                                        </div>
                                                    )
                                                }
                                            />
                                            <DrawerRow label="Directorship" value={showUserProfile?.designation} />
                                            <DrawerRow
                                                label="Tenure"
                                                value={
                                                    showTenureRow ? (
                                                        <span className={profileStyles.TenureValue}>
                                                            {hasTenureDuration && (
                                                                <span className={profileStyles.TenureYears}>{tenureDurationLabel}</span>
                                                            )}
                                                            {tenureRangeLabel && (
                                                                <span className={profileStyles.TenureDates}>{tenureRangeLabel}</span>
                                                            )}
                                                        </span>
                                                    ) : undefined
                                                }
                                            />
                                            <DrawerRow label="DIN No." value={userData?.din} />
                                            <DrawerRow label="PAN" value={userData?.pan} />
                                        </CollapsibleCard>
                                    )}

                                    {showRegulatoryInfoCard && (
                                        <CollapsibleCard
                                            id="regulatory-information-panel"
                                            title="Regulatory Information"
                                            expanded={expandedCards.regulatoryInfo}
                                            onToggle={() => toggleCard('regulatoryInfo')}
                                        >
                                            {/* Every click-through fires RECUR-27110's section event. The PDF branch
                                                already wrapped these; the on-screen rows passed the raw callbacks, so
                                                the event was lost on the path users actually take. */}
                                            <CreditBureauRow
                                                score={userData?.creditScore}
                                                onClick={() => {
                                                    trackDirectorSection('creditBureau');
                                                    redirectToCreditBureau?.(userData?.directorId, userData?.creditBureau);
                                                }}
                                            />
                                            <RegulatoryCountRow
                                                label="Legal Cases"
                                                count={userData?.legalCasesCount}
                                                notReady={!litigationProcessed}
                                                unitSingular="Case"
                                                unitPlural="Cases"
                                                zeroLabel="No match found"
                                                onClickIfPositive={() => {
                                                    trackDirectorSection('legalCases');
                                                    redirectToCompliances?.();
                                                }}
                                            />
                                            <RegulatoryCountRow
                                                label="Risk Alerts"
                                                count={userData?.riskIndicatorCount}
                                                unitSingular="Alert"
                                                unitPlural="Alerts"
                                                zeroLabel="No alerts found"
                                                onClickIfPositive={() => {
                                                    trackDirectorSection('riskIndicators');
                                                    redirectToRiskIndicators?.();
                                                }}
                                            />
                                            <RegulatoryCountRow
                                                label="Regulatory Checks"
                                                count={regulatoryBadge.count}
                                                badge={regulatoryBadge}
                                                locked={regulatoryChecksLocked}
                                                unitSingular="Indicator"
                                                unitPlural="Indicators"
                                                zeroLabel="No indicators found"
                                                onClickIfPositive={() => {
                                                    trackDirectorSection('regulatoryChecks');
                                                    redirectToRegulatoryChecks?.();
                                                }}
                                                mutedLabel
                                                lockedTooltip={LOCKED_COLUMN_TOOLTIPS.regulatoryChecks}
                                            />
                                        </CollapsibleCard>
                                    )}

                                    {showWorkExperienceList && (
                                        <CollapsibleCard
                                            id="work-experience-panel"
                                            title="Work Experience"
                                            expanded={expandedCards.workExperience}
                                            onToggle={() => toggleCard('workExperience')}
                                        >
                                            {userData?.workExperience?.map((exp: WorkEx, i) => {
                                                const fromDate = formatFlexibleDateMonthYear(exp?.startDate);
                                                const toDate = exp?.endDate ? formatFlexibleDateMonthYear(exp?.endDate) : null;
                                                const role = getEntryRole(exp);
                                                const durationLabel = formatPreciseDurationLabel(getEntryDurationMonths(exp, i));
                                                const subline = [role, durationLabel].filter(Boolean).join(' • ') || undefined;
                                                return (
                                                    <ListRow
                                                        key={i}
                                                        icon={
                                                            <CompanyLogoBadge
                                                                name={exp?.companyName}
                                                                logo={exp?.logo}
                                                                size={24}
                                                                shape="square"
                                                                className={profileStyles.DirectorShipLogo}
                                                                fallback={<PlaceholderBadge icon={companyBlack} />}
                                                            />
                                                        }
                                                        name={exp?.companyName || ''}
                                                        dateRange={buildEntryDateRange(fromDate, toDate, i === 0)}
                                                        subline={subline}
                                                    />
                                                );
                                            })}
                                        </CollapsibleCard>
                                    )}

                                    {showEducationList && (
                                        <CollapsibleCard
                                            id="education-panel"
                                            title="Education"
                                            expanded={expandedCards.education}
                                            onToggle={() => toggleCard('education')}
                                        >
                                            {userData?.education?.map((ed: Education, i) => {
                                                const fromDate = formatFlexibleDateMonthYear(ed?.startDate);
                                                const toDate = formatFlexibleDateMonthYear(ed?.endDate);
                                                // Degree, not a job title — see getEducationSubline.
                                                const qualification = getEducationSubline(ed);
                                                const durationLabel = formatPreciseDurationLabel(getEntryDurationMonths(ed, i));
                                                const subline = [qualification, durationLabel].filter(Boolean).join(' • ') || undefined;
                                                return (
                                                    <ListRow
                                                        key={i}
                                                        icon={
                                                            <CompanyLogoBadge
                                                                name={ed?.schoolName}
                                                                logo={ed?.logo}
                                                                size={24}
                                                                shape="square"
                                                                className={profileStyles.DirectorShipLogo}
                                                                fallback={<PlaceholderBadge icon={educationIcon} />}
                                                            />
                                                        }
                                                        name={ed?.schoolName || ''}
                                                        dateRange={buildEntryDateRange(fromDate, toDate)}
                                                        subline={subline}
                                                    />
                                                );
                                            })}
                                        </CollapsibleCard>
                                    )}

                                    {showOtherDirectorshipsList && (
                                        <CollapsibleCard
                                            id="other-directorships-panel"
                                            title="Other Directorships"
                                            expanded={expandedCards.otherDirectorships}
                                            onToggle={() => toggleCard('otherDirectorships')}
                                        >
                                            {!hasOtherDirectorships && <NotAvailableRow />}
                                            {[
                                                ...(userData?.currentEntities || []).map((d) => ({ ...d, isCurrent: true })),
                                                ...(userData?.formerEntities || []).map((d) => ({ ...d, isCurrent: false })),
                                            ].map((directorship, i) => {
                                                if (!directorship?.entityName) return null;
                                                const durationLabel = formatPreciseDurationLabel(
                                                    getFlexibleDurationMonths(
                                                        directorship.fromDate || directorship.dateOfAppointment,
                                                        directorship.toDate,
                                                    ),
                                                );
                                                const role = getEntryRole(directorship as RoleFields);
                                                const subline = [role, durationLabel].filter(Boolean).join(' • ') || undefined;
                                                return (
                                                    <ListRow
                                                        key={`${directorship.entityId || directorship.entityName}-${i}`}
                                                        icon={<PlaceholderBadge letter={directorship.entityName} />}
                                                        name={directorship.entityName}
                                                        subline={subline}
                                                        pill={{
                                                            label: directorship.isCurrent ? 'Active' : 'Resigned',
                                                            active: directorship.isCurrent,
                                                        }}
                                                    />
                                                );
                                            })}
                                        </CollapsibleCard>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
export default ManagementUserProfile;
